import * as React from "react";

import { AuthContext } from "@/auth/AuthContext";
import { accountsApi } from "@/lib/accounts-api";
import { getApiErrorMessage } from "@/lib/error-codes";
import type { AuthState, AuthUser, LoginPayload, RegisterPayload } from "@/types";
import type { Customer } from "@/types/customer";

function customerStub(customerId: number): Customer {
  return {
    customerId,
    email: "",
    phone: "",
    name: "",
    createdAt: "",
    updatedAt: null,
    marketing: false,
    blocked: false,
    blockedBy: null,
    blockedReason: null,
    blockedAt: null,
    customerType: "person",
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AuthState>(() => {
    const hasCustomerSession = accountsApi.hasActiveCustomerSession();
    const customerToken = accountsApi.getCustomerToken();
    const accountToken = accountsApi.getAccountToken();
    const selectedCustomerId = accountsApi.getSelectedCustomerId();

    if (hasCustomerSession && customerToken && selectedCustomerId != null) {
      return {
        isLoading: false,
        isAuthenticated: true,
        token: customerToken,
        user: null,
        customers: [],
        selectedCustomer: customerStub(selectedCustomerId),
        error: null,
      };
    }

    return {
      isLoading: !!accountToken,
      isAuthenticated: false,
      token: null,
      user: null,
      customers: [],
      selectedCustomer: null,
      error: null,
    };
  });

  const applyAuthenticatedState = React.useCallback(
    (token: string, user: AuthUser, customers: Customer[], selectedCustomerId?: number | null) => {
      const selectedCustomer =
        selectedCustomerId == null
          ? customers.length === 1
            ? customers[0]
            : null
          : customers.find((item) => item.customerId === selectedCustomerId) ??
            customerStub(selectedCustomerId);

      setState({
        isLoading: false,
        isAuthenticated: true,
        token,
        user,
        customers,
        selectedCustomer,
        error: null,
      });
    },
    []
  );

  const clearSession = React.useCallback((error: string | null = null) => {
    accountsApi.clearAuth();
    setState({
      isLoading: false,
      isAuthenticated: false,
      token: null,
      user: null,
      customers: [],
      selectedCustomer: null,
      error,
    });
  }, []);

  React.useEffect(() => {
    const customerToken = accountsApi.getCustomerToken();
    const accountToken = accountsApi.getAccountToken();

    if (!customerToken && !accountToken) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }));
      return;
    }

    const selectedCustomerId = accountsApi.getSelectedCustomerId();

    if (selectedCustomerId != null && customerToken) {
      void accountsApi
        .getAccountContext()
        .then(({ user, customers }) => {
          const customerExists = customers.some((item) => item.customerId === selectedCustomerId);

          if (!customerExists) {
            accountsApi.clearSelectedCustomer();
            if (!accountsApi.getAccountToken()) {
              clearSession("Сессия истекла. Войдите снова.");
              return;
            }

            applyAuthenticatedState(accountsApi.getAccountToken()!, user, customers, null);
            return;
          }

          applyAuthenticatedState(customerToken, user, customers, selectedCustomerId);
        })
        .catch(() => {
          clearSession("Сессия истекла. Войдите снова.");
        });
      return;
    }

    if (!accountToken) {
      clearSession();
      return;
    }

    void accountsApi
      .getAccountContext()
      .then(async ({ user, customers }) => {
        if (customers.length === 1) {
          const newCustomerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
          applyAuthenticatedState(newCustomerToken, user, customers, customers[0].customerId);
          return;
        }

        applyAuthenticatedState(accountToken, user, customers, null);
      })
      .catch(() => {
        clearSession("Сессия истекла. Войдите снова.");
      });
  }, [applyAuthenticatedState, clearSession]);

  const register = React.useCallback(
    async (payload: RegisterPayload) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        await accountsApi.register(payload);
        const session = await accountsApi.login({
          email: payload.email,
          password: payload.password,
        });
        const customers = await accountsApi.customers();
        if (customers.length === 1) {
          const customerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
          applyAuthenticatedState(customerToken, session.user, customers, customers[0].customerId);
          return;
        }

        accountsApi.clearSelectedCustomer();
        applyAuthenticatedState(session.token, session.user, customers, null);
      } catch (error) {
        setState({
          isLoading: false,
          isAuthenticated: false,
          token: null,
          user: null,
          customers: [],
          selectedCustomer: null,
          error: getApiErrorMessage(error),
        });
        throw error;
      }
    },
    [applyAuthenticatedState],
  );

  const login = React.useCallback(
    async (payload: LoginPayload) => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const session = await accountsApi.login(payload);
        const customers = await accountsApi.customers();
        if (customers.length === 1) {
          const customerToken = await accountsApi.activateCustomerSession(customers[0].customerId);
          applyAuthenticatedState(customerToken, session.user, customers, customers[0].customerId);
          return;
        }

        accountsApi.clearSelectedCustomer();
        applyAuthenticatedState(session.token, session.user, customers, null);
      } catch (error) {
        setState({
          isLoading: false,
          isAuthenticated: false,
          token: null,
          user: null,
          customers: [],
          selectedCustomer: null,
          error: getApiErrorMessage(error),
        });
        throw error;
      }
    },
    [applyAuthenticatedState]
  );

  const logout = React.useCallback(async () => {
    await accountsApi.logout();
    setState({
      isLoading: false,
      isAuthenticated: false,
      token: null,
      user: null,
      customers: [],
      selectedCustomer: null,
      error: null,
    });
  }, []);

  const selectCustomer = React.useCallback(async (customerId: number) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const customerToken = await accountsApi.activateCustomerSession(customerId);
      const { user, customers } = await accountsApi.getAccountContext();
      applyAuthenticatedState(customerToken, user, customers, customerId);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: getApiErrorMessage(error),
      }));
      throw error;
    }
  }, [applyAuthenticatedState]);

  const switchCustomer = React.useCallback(async () => {
    await accountsApi.deactivateCustomerSession();

    const accountToken = accountsApi.getAccountToken();
    if (!accountToken) {
      clearSession();
      return;
    }

    setState((prev) => ({
      ...prev,
      isLoading: false,
      isAuthenticated: true,
      token: accountToken,
      selectedCustomer: null,
      error: null,
    }));
  }, [clearSession]);

  const value = React.useMemo(
    () => ({
      ...state,
      login,
      register,
      logout,
      selectCustomer,
      switchCustomer,
    }),
    [state, login, register, logout, selectCustomer, switchCustomer]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
